import PropTypes from 'prop-types';

import { withRouter } from 'core/libs/router';
import { Helmet } from 'core/libs/helmet';

import modelPropTypes from 'core/utils/prop-types/model';
import { denormalizeData } from 'core/utils/api';
import {
  checkPageUrlParam,
  pageLinkConstructor,
  getPage,
} from 'core/utils/url-helper';

import Page from 'core/components/Page';
import Paginatron from 'core/components/Paginatron';


function ListPage(props, context) {
  const {
    content: listPageContent,
    rawData,
    meta,
    compactPaging,
    history,
    location,
    title,
    description,
    children,
    limit,
    pagination: CustomPagination,
    skipRcm,
    isLoading,
  } = props;

  const {
    defaultSiteMeta: {
      titleTemplate,
    },
  } = context;

  const content = listPageContent || denormalizeData(rawData);

  const {
    filtered_count: filteredCount = 0,
    search_results: searchResult,
  } = meta || (rawData || {}).meta || {};

  const {
    total = 0,
  } = searchResult || {};

  const count = filteredCount || total;

  /**
   * При округлении обязательно делаем фолбек на `1` в случае если при расчете
   * будет получен `0` из-за отсутсвия данных иначе мы получем бесконечный
   * редирект из-за срабатывания условия `!checkPageUrlParam(location, pageCount)`.
   */
  const pageCount = Math.ceil(count / limit) || 1;

  if (!isLoading && !checkPageUrlParam(location, pageCount)) {
    history.replace(pageLinkConstructor(1, location));
  }

  const page = getPage(location.search);

  const titleTemplateWithPage = page > 1
    ? `${titleTemplate} - страница ${page}`
    : titleTemplate;

  const Pagination = CustomPagination || Paginatron;

  return (
    <Page
      title={title}
      description={description}
      skipRcm={skipRcm}
    >
      <Helmet titleTemplate={titleTemplateWithPage}>
        {page < pageCount && (
          <link rel='next' href={pageLinkConstructor(page + 1, location)} />
        )}
        {page >= 2 && (
          <link rel='prev' href={pageLinkConstructor(page - 1, location)} />
        )}
      </Helmet>
      {children({
        content,
        count,
      })}
      {pageCount > 1 && (
        <Pagination
          active={page}
          total={pageCount}
          compact={compactPaging}
        />
      )}
    </Page>
  );
}

ListPage.defaultProps = {
  children: () => null,
};

ListPage.propTypes = {
  /**
   * Компонент кастомной паджинации
   */
  pagination: PropTypes.func,

  /**
   * Функция в которую будет передан результат запроса данных.
   */
  children: PropTypes.func.isRequired,

  /** Title страницы. */
  title: PropTypes.string,

  /** Description страницы. */
  description: PropTypes.string,

  /**
   * Необходим для правильного определения количества страниц в пагинации.
   * Полагаться на количество топиков в content или rawData нельзя,
   * т.к. последние страницы пагинации могут содержать меньше топиков,
   * что будет приводить к багам в расчёте количества страниц.
   */
  limit: PropTypes.number.isRequired,

  /** Данные денормализованы и готовы к отрисовке */
  content: PropTypes.arrayOf(modelPropTypes(PropTypes.object)),

  /**
   * Сырые НЕденормализованые данные от апишки
   */
  rawData: PropTypes.shape({
    data: PropTypes.array,
    meta: PropTypes.object,
    included: PropTypes.array,
  }),

  /**
   * Поле meta из ответа API или rawData.
   *
   * При передаче content, поле становится обязательным.
   */
  meta: PropTypes.shape({
    filtered_count: PropTypes.number,
    search_result: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),

  /**
   * Отображать компактный пагинатрон.
   */
  compactPaging: PropTypes.bool,

  /**
   * Признак загрузки данных, возвращаемый из хуков react-query.
   * Необходим для правильной проверки get-параметра `page`.
   * Если его не передать, при переходах по пагинации может срабатывать
   * `history.replace`.
   */
  isLoading: props => {
    const isLoadingType = typeof props.isLoading;
    if (process.env.NODE_ENV !== 'test' && isLoadingType !== 'boolean') {
      return new Error(`ListPage.isLoading is of type ${isLoadingType}. It must be boolean. Please check components that are using it to avoid pagination problems in browser.`);
    }
    return null;
  },

  /** Отключить отправку событий RCM. */
  skipRcm: PropTypes.bool,

  /** @ignore */
  location: PropTypes.object.isRequired,

  /** @ignore */
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};

ListPage.contextTypes = {
  defaultSiteMeta: PropTypes.shape({
    titleTemplate: PropTypes.string,
  }).isRequired,
};

const ListPageWithHOCs = withRouter(ListPage);

ListPageWithHOCs.displayName = 'ListPage';

export { ListPage as StorybookComponent };
export default ListPageWithHOCs;
